import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Wallet, CheckCircle, XCircle, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import BulkApproveButton from "./BulkApproveButton";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusMap = {
  pending: { label: "بانتظار الموافقة", color: "#F5A623", icon: Clock },
  approved: { label: "تمت الموافقة", color: "#7ED321", icon: CheckCircle },
  rejected: { label: "مرفوض", color: "#E85D45", icon: XCircle },
};

const WithdrawalsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/withdrawals`);
      setItems(data);
    } catch {
      toast.error("فشل تحميل طلبات السحب");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async (id, action) => {
    if (action === "approve" && !await confirmDialog("هل أنت متأكد من الموافقة على طلب السحب وخصم المبلغ من الرصيد؟")) return;
    if (action === "reject" && !await confirmDialog("هل أنت متأكد من رفض هذا الطلب؟")) return;
    setProcessing(id);
    try {
      await axios.patch(`${API}/admin/withdrawals/${id}`, { action });
      toast.success(action === "approve" ? "تمت الموافقة على السحب" : "تم رفض الطلب");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تنفيذ الإجراء");
    } finally {
      setProcessing(null);
    }
  };

  const filtered = filter === "all" ? items : items.filter((w) => w.status === filter);
  const pendingCount = items.filter((w) => w.status === "pending").length;

  return (
    <div data-testid="withdrawals-tab" className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <Wallet className="w-6 h-6 text-[#2D4A3E]" />
          طلبات السحب
          {pendingCount > 0 && (
            <span className="bg-[#E85D45] text-white text-xs px-2.5 py-0.5 rounded-full font-bold animate-pulse">
              {pendingCount} جديد
            </span>
          )}
        </h2>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { key: "pending", label: "بانتظار الموافقة" },
            { key: "approved", label: "تمت الموافقة" },
            { key: "rejected", label: "مرفوض" },
            { key: "all", label: "الكل" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`wd-filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f.key
                  ? "bg-[#2D4A3E] text-white"
                  : "bg-white text-[#5C6E64] border border-[#E4EBE6] hover:bg-[#F3F6F4]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <BulkApproveButton resource="withdrawals" label="موافقة على الكل" count={pendingCount} onDone={fetchData} />
      </div>

      {loading ? (
        <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E4EBE6]">
          <Wallet className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا توجد طلبات سحب</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="wd-list">
          {filtered.map((w, idx) => {
            const StatusIcon = statusMap[w.status]?.icon || Clock;
            const statusColor = statusMap[w.status]?.color || "#999";
            return (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                data-testid={`wd-${w.id}`}
                className="bg-white border border-[#E4EBE6] rounded-2xl p-5 space-y-3"
              >
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-[#111814] text-lg">{w.market_name || w.user_name}</p>
                    <a href={`tel:${w.user_phone}`} className="text-sm text-[#E85D45] flex items-center gap-1 hover:underline">
                      <Phone className="w-3 h-3" />
                      {w.user_phone}
                    </a>
                    <p className="text-xs text-[#5C6E64] mt-1">{formatDate(w.created_at)}</p>
                  </div>
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5"
                    style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {statusMap[w.status]?.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-[#F3F6F4] rounded-xl p-3">
                  <div>
                    <p className="text-xs text-[#5C6E64]">المبلغ المطلوب</p>
                    <p className="font-bold text-xl text-[#E85D45]">{fmt(w.amount)} د.ع</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5C6E64]">الرصيد وقت الطلب</p>
                    <p className="font-bold text-lg text-[#2D4A3E]">{fmt(w.balance_at_request)} د.ع</p>
                  </div>
                </div>

                {w.note && (
                  <div className="bg-[#FFF8E1] border border-[#F5A623]/30 rounded-xl px-3 py-2">
                    <p className="text-xs text-[#5C6E64] mb-1">ملاحظة الزبون:</p>
                    <p className="text-sm italic">{w.note}</p>
                  </div>
                )}

                {w.status === "pending" && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleAction(w.id, "approve")}
                      disabled={processing === w.id}
                      data-testid={`wd-approve-${w.id}`}
                      className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 rounded-full text-sm transition-colors disabled:opacity-50"
                    >
                      ✓ موافقة وخصم
                    </button>
                    <button
                      onClick={() => handleAction(w.id, "reject")}
                      disabled={processing === w.id}
                      data-testid={`wd-reject-${w.id}`}
                      className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm transition-colors disabled:opacity-50"
                    >
                      ✕ رفض
                    </button>
                  </div>
                )}
                {w.processed_at && (
                  <p className="text-xs text-[#5C6E64] pt-2 border-t border-[#E4EBE6]">
                    تمت المعالجة: {formatDate(w.processed_at)}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WithdrawalsTab;
